// Pure, framework-free (de)serialization + immutable editing helpers for a JobSyncConfig's rule data.
// Every function is a pure transform: it takes the raw config shapes (Condition / ValueSpec / Rule /
// RuleSet / fieldMappings) and returns a NEW value, never mutating its input. The raw shapes ARE the
// source of truth — there is no parallel "form model" that could drift from the emitted JSON.
//
// Runtime contract (process-jobs/src/services/ruleEngine.ts + types/config.ts):
//   Condition = { field, operator, value?, caseInsensitive? }   // value omitted for is_null/is_not_null
//   ValueSpec = { literal } | { template } | { copyFrom }        // exactly one key
//   Rule      = { when: Condition[], set: ValueSpec }
//   RuleSet   = { rules: Rule[], default?: ValueSpec }
//   FieldMapping = ({ from } | { const } | { ruleSet }) & { rewrite?: RewriteOp[] }  // one base key + optional rewrite
//   RewriteOp    = { append } | { prepend } | { replaceSuffix: { find, with } } | { split: { sep, index } }
//                | { firstNonNull: {} }   // string post-processors + the one array-aware selector

import { valueKind } from './operators';

// ----------------------------------------------------------------------------
// Conditions
// ----------------------------------------------------------------------------

export function makeCondition(field = '', operator = 'equals') {
	const c = { field, operator };
	const kind = valueKind(operator);
	if (kind === 'array') c.value = [];
	else if (kind === 'scalar') c.value = '';
	// kind === 'none' -> no `value` key at all
	return c;
}

export function setConditionField(condition, field) {
	return { ...condition, field };
}

// Change a condition's operator, coercing `value` into the new operator's arity so the emitted JSON
// can never disagree with the operator. Callers (ConditionEditor) may layer their own transient
// "recall" of a dropped value on top; this function stays deterministic.
export function setConditionOperator(condition, operator) {
	const next = { ...condition, operator };
	const kind = valueKind(operator);
	if (kind === 'none') {
		delete next.value;
	} else if (kind === 'array') {
		next.value = toArrayValue(condition.value);
	} else {
		next.value = toScalarValue(condition.value);
	}
	return next;
}

// Set the value for a scalar/array condition. Not used for none-arity operators (they have no value).
export function setConditionValue(condition, value) {
	return { ...condition, value };
}

// caseInsensitive is emitted ONLY when true — the engine checks `=== true`, so `false` is equivalent
// to absent, and emitting `caseInsensitive: false` would break a byte-faithful round-trip.
export function setConditionCaseInsensitive(condition, ci) {
	const next = { ...condition };
	if (ci) next.caseInsensitive = true;
	else delete next.caseInsensitive;
	return next;
}

function toArrayValue(v) {
	if (Array.isArray(v)) return v;
	if (v === undefined || v === null || v === '') return [];
	return [v];
}

function toScalarValue(v) {
	if (Array.isArray(v)) return v.length ? v[0] : '';
	if (v === undefined) return '';
	return v;
}

// ----------------------------------------------------------------------------
// ValueSpec (literal | template | copyFrom) — always exactly one key
// ----------------------------------------------------------------------------

export const VALUE_SPEC_KINDS = ['literal', 'template', 'copyFrom'];

export function valueSpecKind(spec) {
	if (spec && Object.prototype.hasOwnProperty.call(spec, 'literal')) return 'literal';
	if (spec && Object.prototype.hasOwnProperty.call(spec, 'template')) return 'template';
	if (spec && Object.prototype.hasOwnProperty.call(spec, 'copyFrom')) return 'copyFrom';
	return 'literal';
}

// Read a spec into { kind, value } for a control. `literal` values are returned verbatim (type
// preserved); `template`/`copyFrom` are strings.
export function readValueSpec(spec) {
	const kind = valueSpecKind(spec);
	return { kind, value: spec ? spec[kind] : '' };
}

export function makeValueSpec(kind, value) {
	if (kind === 'template') return { template: value == null ? '' : String(value) };
	if (kind === 'copyFrom') return { copyFrom: value == null ? '' : String(value) };
	// literal — preserve the value's type verbatim (string, number, boolean, …)
	return { literal: value };
}

// ----------------------------------------------------------------------------
// Rules
// ----------------------------------------------------------------------------

export function makeRule() {
	return { when: [makeCondition()], set: { literal: '' } };
}

export function addCondition(rule) {
	return { ...rule, when: [...rule.when, makeCondition()] };
}

export function removeCondition(rule, index) {
	return { ...rule, when: rule.when.filter((_, i) => i !== index) };
}

export function updateCondition(rule, index, condition) {
	return { ...rule, when: rule.when.map((c, i) => (i === index ? condition : c)) };
}

export function setRuleSpec(rule, spec) {
	return { ...rule, set: spec };
}

// ----------------------------------------------------------------------------
// RuleSets (ordered; first match wins; optional default)
// ----------------------------------------------------------------------------

export function makeRuleSet() {
	return { rules: [] };
}

export function addRule(ruleSet, rule = makeRule()) {
	return { ...ruleSet, rules: [...ruleSet.rules, rule] };
}

export function removeRule(ruleSet, index) {
	return { ...ruleSet, rules: ruleSet.rules.filter((_, i) => i !== index) };
}

export function updateRule(ruleSet, index, rule) {
	return { ...ruleSet, rules: ruleSet.rules.map((r, i) => (i === index ? rule : r)) };
}

// Move a rule from one index to another; order encodes precedence. Out-of-range / no-op returns input.
export function moveRule(ruleSet, from, to) {
	const { rules } = ruleSet;
	if (from === to || from < 0 || to < 0 || from >= rules.length || to >= rules.length) return ruleSet;
	const next = rules.slice();
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return { ...ruleSet, rules: next };
}

// The default (`otherwise`) branch. Omitted entirely when cleared (never `default: undefined`).
export function setDefault(ruleSet, spec) {
	return { ...ruleSet, default: spec };
}

export function clearDefault(ruleSet) {
	const next = { ...ruleSet };
	delete next.default;
	return next;
}

export function hasDefault(ruleSet) {
	return !!ruleSet && Object.prototype.hasOwnProperty.call(ruleSet, 'default');
}

// ----------------------------------------------------------------------------
// fieldMappings (Record<erinDotPath, FieldMapping>) <-> ordered rows
// Dot-path keys (department.name, location.city) stay LITERAL string keys — never split/nested.
// Object key insertion order is preserved by rebuilding the record in row order.
// ----------------------------------------------------------------------------

export const FIELD_SOURCE_KINDS = ['from', 'const', 'ruleSet'];

export function fieldMappingKind(mapping) {
	if (mapping && Object.prototype.hasOwnProperty.call(mapping, 'from')) return 'from';
	if (mapping && Object.prototype.hasOwnProperty.call(mapping, 'const')) return 'const';
	if (mapping && Object.prototype.hasOwnProperty.call(mapping, 'ruleSet')) return 'ruleSet';
	return 'from';
}

export function readFieldMapping(mapping) {
	const kind = fieldMappingKind(mapping);
	return { kind, value: mapping ? mapping[kind] : '' };
}

export function makeFieldMapping(kind, value) {
	if (kind === 'const') return { const: value }; // preserve type verbatim (number/boolean/string)
	if (kind === 'ruleSet') return { ruleSet: value == null ? '' : String(value) };
	return { from: value == null ? '' : String(value) };
}

export function fieldMappingsToRows(fieldMappings) {
	return Object.keys(fieldMappings || {}).map((key) => ({ key, mapping: fieldMappings[key] }));
}

export function rowsToFieldMappings(rows) {
	const out = {};
	for (const { key, mapping } of rows) {
		if (key === '' || key == null) continue; // skip blank rows being edited
		out[key] = mapping;
	}
	return out;
}

// ----------------------------------------------------------------------------
// rewrite — the optional post-processor on a field mapping (RewriteOp[])
// Ops run in order over the mapping's resolved value. The base kind (from/const/ruleSet) is
// ORTHOGONAL to the rewrite, so changing the base must not drop it (see setFieldMappingBase).
// ----------------------------------------------------------------------------

export const REWRITE_OP_KINDS = ['append', 'prepend', 'replaceSuffix', 'split', 'firstNonNull'];

export function fieldMappingRewrite(mapping) {
	return mapping && Array.isArray(mapping.rewrite) ? mapping.rewrite : [];
}

export function rewriteOpKind(op) {
	if (op && Object.prototype.hasOwnProperty.call(op, 'append')) return 'append';
	if (op && Object.prototype.hasOwnProperty.call(op, 'prepend')) return 'prepend';
	if (op && Object.prototype.hasOwnProperty.call(op, 'replaceSuffix')) return 'replaceSuffix';
	if (op && Object.prototype.hasOwnProperty.call(op, 'split')) return 'split';
	if (op && Object.prototype.hasOwnProperty.call(op, 'firstNonNull')) return 'firstNonNull';
	return 'append';
}

// Flatten an op into control fields: { kind, text, find, replacement, sep, index }. `with` is a
// reserved word, so it surfaces as `replacement`. append/prepend use `text`; replaceSuffix uses
// `find` + `replacement`; split uses `sep` + `index` (value.split(sep)[index], trimmed);
// firstNonNull is parameterless (array → first non-null element; split and firstNonNull are the
// two ops that can UNSET a field).
export function readRewriteOp(op) {
	const kind = rewriteOpKind(op);
	if (kind === 'firstNonNull') {
		return { kind, text: '', find: '', replacement: '', sep: '', index: 0 };
	}
	if (kind === 'replaceSuffix') {
		const rs = (op && op.replaceSuffix) || {};
		return { kind, text: '', find: rs.find == null ? '' : String(rs.find), replacement: rs.with == null ? '' : String(rs.with), sep: '', index: 0 };
	}
	if (kind === 'split') {
		const sp = (op && op.split) || {};
		return { kind, text: '', find: '', replacement: '', sep: sp.sep == null ? '' : String(sp.sep), index: Number.isInteger(sp.index) ? sp.index : 0 };
	}
	const v = op ? op[kind] : '';
	return { kind, text: v == null ? '' : String(v), find: '', replacement: '', sep: '', index: 0 };
}

export function makeRewriteOp(kind) {
	if (kind === 'prepend') return { prepend: '' };
	if (kind === 'replaceSuffix') return { replaceSuffix: { find: '', with: '' } };
	if (kind === 'split') return { split: { sep: ',', index: 0 } };
	if (kind === 'firstNonNull') return { firstNonNull: {} };
	return { append: '' };
}

// Build an op from control fields (the inverse of readRewriteOp).
export function writeRewriteOp(kind, { text = '', find = '', replacement = '', sep = '', index = 0 } = {}) {
	if (kind === 'prepend') return { prepend: String(text) };
	if (kind === 'replaceSuffix') return { replaceSuffix: { find: String(find), with: String(replacement) } };
	if (kind === 'split') return { split: { sep: String(sep), index: Number.isInteger(Number(index)) ? Number(index) : 0 } };
	if (kind === 'firstNonNull') return { firstNonNull: {} }; // parameterless — control fields are ignored
	return { append: String(text) };
}

// Attach/replace a mapping's rewrite pipeline; the key is OMITTED when empty so an untouched mapping
// stays byte-faithful (never `rewrite: []`).
export function setFieldMappingRewrite(mapping, ops) {
	const next = { ...mapping };
	if (ops && ops.length) next.rewrite = ops;
	else delete next.rewrite;
	return next;
}

// Change a mapping's base (from/const/ruleSet) while KEEPING any attached rewrite — the fix for the
// editor silently dropping a rewrite when a row's source field or kind is edited.
export function setFieldMappingBase(mapping, kind, value) {
	const base = makeFieldMapping(kind, value);
	const ops = fieldMappingRewrite(mapping);
	if (ops.length) base.rewrite = ops;
	return base;
}
